/**
 * Minimal, safe markdown for AI answers: paragraphs, "-"/"*"/"1." lists, **bold**, *italic*, `code`
 * and [n] citation markers. Builds React elements only — never injects HTML.
 */
import { Fragment, type ReactNode } from 'react';

function inline(text: string, key: string, onCite?: (n: number) => void): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*\s][^*]*\*|`[^`]+`|\[\d+\])/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const k = `${key}-${i++}`;
    if (tok.startsWith('**')) out.push(<strong key={k}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={k} className="rounded-sm bg-surface-3 px-1 font-mono text-12">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('[')) {
      const n = Number(tok.slice(1, -1));
      out.push(
        onCite ? (
          <button key={k} type="button" onClick={() => onCite(n)} className="mx-0.5 align-super text-10 font-semibold text-primary-text hover:underline">
            [{n}]
          </button>
        ) : (
          <sup key={k} className="text-10 font-semibold text-primary-text">[{n}]</sup>
        ),
      );
    } else out.push(<em key={k}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ text, onCite, className }: { text: string; onCite?: (n: number) => void; className?: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) {
      const k = `p${blocks.length}`;
      blocks.push(<p key={k} className="m-0">{inline(para.join(' '), k, onCite)}</p>);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      const k = `l${blocks.length}`;
      const items = list.items.map((it, i) => <li key={i}>{inline(it, `${k}-${i}`, onCite)}</li>);
      blocks.push(
        list.ordered ? (
          <ol key={k} className="m-0 list-decimal ps-5">{items}</ol>
        ) : (
          <ul key={k} className="m-0 list-disc ps-5">{items}</ul>
        ),
      );
      list = null;
    }
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const heading = /^#{1,4}\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushPara();
      const ordered = !!numbered;
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push((bullet ?? numbered)![1]!);
    } else if (heading) {
      flushPara();
      flushList();
      const k = `h${blocks.length}`;
      blocks.push(<p key={k} className="m-0 font-semibold">{inline(heading[1]!, k, onCite)}</p>);
    } else if (!line.trim()) {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();
  return <div className={className ?? 'flex flex-col gap-2'}>{blocks.map((b, i) => <Fragment key={i}>{b}</Fragment>)}</div>;
}
